import { log, sendExternalLogs, updateDebugPanelCell } from "./debug-utils.js";

/**
 * Get URL parameters
 * @returns {Object} Parameters as key-value pairs
 */
export function getUrlParams() {
  const params = {};
  const searchParams = new URLSearchParams(window.location.search);

  for (const [key, value] of searchParams.entries()) {
    if (key == "fbclid") {
      const fbcValue = prepareFbcParam(value);
      params["fbc"] = fbcValue;
      continue;
    }
    params[key] = value;
  }

  log("URL parameters retrieved", "info", params);
  return params;
}

export function prepareFbcParam(fbclid) {
  return "fb.1." + Date.now() + "." + fbclid;
}

/**
 * Store parameters in sessionStorage
 * @param {Object} params - Parameters to store
 * @param {string} storageKey - Storage key
 * @returns {boolean} - Success state
 */
export function storeParams(params, storageKey) {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(params));
    log(`Parameters stored: ${Object.keys(params).join(", ")}`, "info", params);
    return true;
  } catch (e) {
    log("Failed to store params in sessionStorage", "error", e);
    return false;
  }
}

/**
 * Get stored parameters from sessionStorage
 * @param {string} storageKey - Storage key
 * @returns {Object|null} Stored parameters
 */
export function getStoredParams(storageKey) {
  try {
    const stored = sessionStorage.getItem(storageKey);
    const params = stored ? JSON.parse(stored) : null;
    log(
      `Retrieved parameters from sessionStorage (${storageKey})`,
      "info",
      params
    );
    return params;
  } catch (e) {
    log("Failed to retrieve params from sessionStorage", "error", e);
    return null;
  }
}

/**
 * Check if we should track this session
 * @param {Object} params - URL parameters
 * @param {string} requiredParam - Required parameter name
 * @param {string} requiredValue - Required parameter value
 * @returns {boolean} Should track
 */
export function shouldTrackSession(params, requiredParam, requiredValue) {
  const shouldTrack =
    !!params &&
    !!params[requiredParam] &&
    params[requiredParam] === requiredValue;

  log(
    shouldTrack
      ? `Tracking enabled: ${requiredParam}=${requiredValue} found`
      : `Tracking disabled: Required parameter not found`,
    shouldTrack ? "info" : "warn",
    {
      params,
      requiredParam,
      requiredValue,
      shouldTrack,
    }
  );

  return shouldTrack;
}

/**
 * Mark event as sent in this session
 * @param {string} storageKey - Storage key
 * @returns {boolean} - Success state
 */
export function markEventSent(storageKey) {
  try {
    sessionStorage.setItem(storageKey, "true");
    log(`Event marked as sent (${storageKey})`, "info");
    return true;
  } catch (e) {
    log("Failed to mark event as sent in sessionStorage", "error", e);
    return false;
  }
}

/**
 * Get consent status
 * @param {string} storageKey - Storage key
 * @returns {boolean} - Consent status
 */
export function getConsentStatus(storageKey) {
  const hasConsent = sessionStorage.getItem(storageKey) === "true";
  log(`Retrieved consent status (${storageKey})`, "info", { hasConsent });
  return hasConsent;
}

/**
 * Set consent status
 * @param {boolean} hasConsent - Consent state
 * @param {string} storageKey - Storage key
 * @returns {boolean} - Success state
 */
export function setConsentStatus(hasConsent, storageKey) {
  try {
    sessionStorage.setItem(storageKey, hasConsent ? "true" : "false");
    log(`Consent status set to: ${hasConsent ? "granted" : "denied"}`, "info", {
      hasConsent,
    });
    return true;
  } catch (e) {
    log("Failed to store consent status in sessionStorage", "error", e);
    return false;
  }
}

/**
 * Detects if the page is currently unloading/closing
 * @returns {boolean} - True if page is unloading
 */
function isPageUnloading() {
  return (
    document.visibilityState === "hidden" || document.readyState === "unloading"
  );
}

/**
 * Send data using XMLHttpRequest
 * @param {string} jsonData - Stringified JSON data
 * @param {string} endpoint - Server endpoint
 * @param {string} sentKey - Storage key for marking event as sent
 * @returns {Object|null} - Result object or null if failed
 */
function sendViaXhr(jsonData, endpoint, sentKey) {
  try {
    const unloadingPage = isPageUnloading();
    log(`Sending data via XMLHttpRequest to ${endpoint}`, "info", {
      endpoint,
      unloadingPage,
    });

    // Only for development
    const urlParams = getUrlParams();
    const xGTMpreviewHeaderValue = urlParams.get("gtm-preview") ?? false;

    const xhr = new XMLHttpRequest();

    // Use synchronous XHR during unload events for better reliability
    xhr.open("POST", endpoint, !unloadingPage);
    xhr.setRequestHeader("Content-Type", "application/json");
    if (xGTMpreviewHeaderValue) {
      xhr.setRequestHeader("x-gtm-server-preview", xGTMpreviewHeaderValue);
    }

    if (!unloadingPage) {
      // For normal operation, set up async handling
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          markEventSent(sentKey);
          log(`XMLHttpRequest succeeded: ${xhr.status}`, "info", {
            status: xhr.status,
          });
        } else {
          log(`XMLHttpRequest failed: ${xhr.status}`, "error", {
            status: xhr.status,
          });
        }
      };
    }

    xhr.send(jsonData);

    if (unloadingPage && xhr.status >= 200 && xhr.status < 300) {
      markEventSent(sentKey);
      log("Synchronous XMLHttpRequest succeeded", "info", {
        status: xhr.status,
      });
    }

    return { ok: true, method: "xhr" };
  } catch (error) {
    log("XMLHttpRequest failed", "error", error);
    return null;
  }
}

/**
 * Send data using navigator.sendBeacon
 * @param {string} jsonData - Stringified JSON data
 * @param {string} endpoint - Server endpoint
 * @param {string} sentKey - Storage key for marking event as sent
 * @returns {Object|null} - Result object or null if failed
 */
function sendViaBeacon(jsonData, endpoint, sentKey) {
  if (!navigator.sendBeacon) {
    log("sendBeacon not supported", "warn");
    return null;
  }

  try {
    log(`Sending data via Beacon to ${endpoint}`, "info", { endpoint });

    let beaconSuccess = false;

    // Try with Blob first (preferred method)
    try {
      const blob = new Blob([jsonData], { type: "application/json" });
      beaconSuccess = navigator.sendBeacon(endpoint, blob);
      log("sendBeacon with Blob", "info", { success: beaconSuccess });
    } catch (blobError) {
      // If Blob fails due to CSP, try with string directly
      log("Blob construction failed, trying with string", "warn", blobError);
      beaconSuccess = navigator.sendBeacon(endpoint, jsonData);
      log("sendBeacon with string", "info", { success: beaconSuccess });
    }

    if (beaconSuccess) {
      markEventSent(sentKey);
      return { ok: true, method: "beacon" };
    }
  } catch (error) {
    log("sendBeacon failed", "error", error);
  }

  return null;
}

/**
 * Send data using fetch API
 * @param {string} jsonData - Stringified JSON data
 * @param {string} endpoint - Server endpoint
 * @param {string} sentKey - Storage key for marking event as sent
 * @returns {Promise<Object>} - Promise resolving to result object
 */
async function sendViaFetch(jsonData, endpoint, sentKey) {
  log(`Sending data via fetch to ${endpoint}`, "info", { endpoint });

  // Only for development
  const urlParams = getUrlParams();
  const xGTMpreviewHeaderValue = urlParams.get("gtm-preview") ?? false;

  const headers = { "Content-Type": "application/json" };

  if (xGTMpreviewHeaderValue) {
    headers["x-gtm-server-preview"] = xGTMpreviewHeaderValue;
  }

  const response = await fetch(endpoint, {
    method: "POST",
    headers: headers,
    body: jsonData,
    keepalive: true,
    credentials: "same-origin",
  });

  if (response.ok) {
    markEventSent(sentKey);
    log(`Fetch succeeded: ${response.status}`, "info", {
      status: response.status,
    });
    return { ok: true, method: "fetch", response };
  }

  log(`Fetch failed: ${response.status}`, "error", { status: response.status });
  throw new Error(`Server responded with ${response.status}`);
}

/**
 * Send data using image beacon as final fallback
 * @param {string} jsonData - Stringified JSON data
 * @param {string} endpoint - Server endpoint
 * @param {string} sentKey - Storage key for marking event as sent
 * @returns {Object} - Result object
 */
function sendViaImageBeacon(jsonData, endpoint, sentKey) {
  log("Attempting to send data via image beacon (fallback)", "info", {
    endpoint,
  });

  const imgBeacon = new Image();
  const compressedData = encodeURIComponent(jsonData).substring(0, 2000);
  imgBeacon.src = `${endpoint}?data=${compressedData}&t=${Date.now()}`;

  // Can't reliably know if this succeeded, but mark as sent
  markEventSent(sentKey);
  return { ok: true, method: "img-fallback", complete: false };
}

/**
 * Extract parameters from referrer URL
 * @returns {Object} Parameters extracted from referrer
 */
export function getReferrerParams() {
  const params = {};

  if (!document.referrer) {
    log("No referrer available", "info");
    return params;
  }

  try {
    const referrerUrl = new URL(document.referrer);
    const searchParams = new URLSearchParams(referrerUrl.search);

    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }

    log(
      `Parameters recovered from referrer: ${Object.keys(params).join(", ")}`,
      "info",
      {
        referrer: document.referrer,
        params,
      }
    );
  } catch (e) {
    log("Failed to parse referrer URL", "error", e);
  }

  return params;
}

/**
 * Get parameters with fallback to referrer
 * @param {string} storageKey - Storage key for stored params
 * @returns {Object} Combined parameters from URL, storage, and referrer
 */
export function getParamsWithFallback(storageKey) {
  // Get URL parameters
  const urlParams = getUrlParams();

  // If we have URL parameters, use them
  if (Object.keys(urlParams).length > 0) {
    log("Using parameters from URL", "info", urlParams);
    return urlParams;
  }

  // Fallback to referrer parameters
  const referrerParams = getReferrerParams();
  if (Object.keys(referrerParams).length > 0) {
    log("Using parameters from referrer", "info", referrerParams);
    // Store referrer params for future use
    storeParams(referrerParams, storageKey);
    return referrerParams;
  }

  log("No parameters found in URL, storage, or referrer", "warn");
  return {};
}

/**
 * Store failed event in localStorage for retry
 * @param {Object} eventData - Event data to store
 * @param {string} endpoint - Server endpoint
 * @returns {boolean} - Success state
 */
export function storeFailedEvent(eventData, endpoint) {
  try {
    const failedEvents = getFailedEvents();
    const eventWithMeta = {
      ...eventData,
      _meta: {
        endpoint,
        timestamp: Date.now(),
        retryCount: 0,
      },
    };

    failedEvents.push(eventWithMeta);
    localStorage.setItem("_vt_failed_events", JSON.stringify(failedEvents));

    log("Failed event stored for retry", "info", eventWithMeta);
    return true;
  } catch (e) {
    log("Failed to store failed event", "error", e);
    return false;
  }
}

/**
 * Get failed events from localStorage
 * @returns {Array} - Array of failed events
 */
export function getFailedEvents() {
  try {
    const stored = localStorage.getItem("_vt_failed_events");
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    log("Failed to retrieve failed events", "error", e);
    return [];
  }
}

export function storeEventLocalStorage(eventData) {
  try {
    localStorage.setItem("_vt_event_data", JSON.stringify(eventData));
    log("Event data stored in LocalStorage", "info", eventData);
    return true;
  } catch (e) {
    log("Failed to store event data in LocalStorage", "error", e);
    return false;
  }
}

export function getEventLocalStorage() {
  try {
    const stored = localStorage.getItem("_vt_event_data");
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    log("Failed to retrieve event data from LocalStorage", "error", e);
    return [];
  }
}

export function clearEventLocalStorage() {
  try {
    localStorage.removeItem("_vt_event_data");
    log("Event data cleared from LocalStorage", "info");
    return true;
  } catch (e) {
    log("Failed to clear event data in LocalStorage", "error", e);
    return false;
  }
}

/**
 * Clear failed events from localStorage
 * @returns {boolean} - Success state
 */
export function clearFailedEvents() {
  try {
    localStorage.removeItem("_vt_failed_events");
    log("Failed events cleared", "info");
    return true;
  } catch (e) {
    log("Failed to clear failed events", "error", e);
    return false;
  }
}

/**
 * Retry failed events from localStorage
 * @param {string} sentKey - Storage key for marking events as sent
 * @returns {Promise<void>}
 */
export async function retryFailedEvents(sentKey) {
  const failedEvents = getFailedEvents();

  if (failedEvents.length === 0) {
    log("No failed events to retry", "info");
    return;
  }

  log(`Retrying ${failedEvents.length} failed events`, "info");

  const successfulEvents = [];
  const stillFailedEvents = [];

  for (const event of failedEvents) {
    try {
      // Skip events that have been retried too many times
      if (event._meta.retryCount >= 2) {
        log("Event exceeded retry limit, discarding", "warn", event);
        continue;
      }

      // Try to send the event
      const result = await sendToSgtm(
        event,
        event._meta.endpoint,
        sentKey,
        false
      );

      if (result) {
        successfulEvents.push(event);
        log("Failed event successfully retried", "info");
      } else {
        // Increment retry count and keep for next attempt
        event._meta.retryCount++;
        stillFailedEvents.push(event);
      }
    } catch (error) {
      log("Error retrying failed event", "error", error);
      // Increment retry count and keep for next attempt
      event._meta.retryCount++;
      stillFailedEvents.push(event);
    }
  }

  // Update localStorage with events that still failed
  if (stillFailedEvents.length > 0) {
    try {
      localStorage.setItem(
        "_vt_failed_events",
        JSON.stringify(stillFailedEvents)
      );
      log(
        `${stillFailedEvents.length} events still failed, stored for next retry`,
        "warn"
      );
    } catch (e) {
      log("Failed to update failed events storage", "error", e);
    }
  } else {
    clearFailedEvents();
  }

  log(
    `Retry summary: ${successfulEvents.length} succeeded, ${stillFailedEvents.length} still failed`,
    "info"
  );
}

// Update the existing sendToSgtm function with retry logic
/**
 * Send event data to sGTM with retry logic
 * @param {Object} data - Event data
 * @param {string} endpoint - Server endpoint
 * @param {string} sentKey - Storage key for marking event as sent
 * @param {boolean} storeOnFailure - Whether to store failed events for retry
 * @returns {Promise<Object>} - Promise resolving to result object
 */
export async function sendToSgtm(
  data,
  endpoint,
  sentKey,
  storeOnFailure = true
) {
  const maxRetries = 2;
  let lastError = null;
  const eventStatus = document.getElementById("vtd-session-event-status");
  const eventStatusValue = document.getElementById(
    "vtd-session-event-status-value"
  );

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      log(`Sending attempt ${attempt}/${maxRetries}`, "info", { endpoint });

      const result = await sendToSgtmSingle(data, endpoint, sentKey);

      if (result) {
        if (attempt > 1) {
          log(`Successfully sent on attempt ${attempt}`, "info");
        }

        const jsonData = JSON.stringify(data);
        sendExternalLogs(jsonData);

        if (eventStatus && eventStatusValue) {
          updateDebugPanelCell("vtd-session-event-status-value", "Success", [
            "bg-green",
          ]);
        }

        return result;
      }
    } catch (error) {
      lastError = error;
      log(`Attempt ${attempt} failed`, "error", error);

      if (eventStatus && eventStatusValue) {
        updateDebugPanelCell("vtd-session-event-status-value", "Failed", [
          "bg-orange",
        ]);
      }

      // Wait before retry (exponential backoff: 1s, 2s)
      if (attempt < maxRetries) {
        const delay = attempt * 1000;
        log(`Waiting ${delay}ms before retry`, "info");
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  // All attempts failed
  log("All send attempts failed", "error", lastError);

  // Store for retry on next page load if enabled
  if (storeOnFailure) {
    storeFailedEvent(data, endpoint);
  }

  throw lastError || new Error("All send attempts failed");
}

// Rename the existing sendToSgtm function
export async function sendToSgtmSingle(data, endpoint, sentKey) {
  log("Preparing to send event data to sGTM", "info", { data, endpoint });

  // Convert data to JSON string once for all methods
  const jsonData = JSON.stringify(data);
  log("Event data serialized", "info", { byteLength: jsonData.length });

  // Try methods in order of preference

  // 1. XMLHttpRequest for best CSP compatibility
  if (window.XMLHttpRequest && typeof endpoint === "string") {
    const xhrResult = sendViaXhr(jsonData, endpoint, sentKey);
    if (xhrResult) {
      log("Successfully sent via XMLHttpRequest", "info");
      return xhrResult;
    }
  }

  // 2. SendBeacon API for best reliability during page unload
  const beaconResult = sendViaBeacon(jsonData, endpoint, sentKey);
  if (beaconResult) {
    log("Successfully sent via sendBeacon", "info");
    return beaconResult;
  }

  // 3. Fetch API for modern environments
  try {
    const fetchResult = await sendViaFetch(jsonData, endpoint, sentKey);
    log("Successfully sent via fetch", "info");
    return fetchResult;
  } catch (fetchError) {
    log("Fetch failed", "error", fetchError);
    // 4. Image beacon as absolute last resort
    try {
      log("Trying image beacon as last resort", "warn");
      const imgResult = sendViaImageBeacon(jsonData, endpoint, sentKey);
      log("Successfully sent via image beacon", "info");
      return imgResult;
    } catch (imgError) {
      log("All transport methods failed", "error", imgError);
      throw new Error("All transport methods failed");
    }
  }
}

export function formatTime(seconds) {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return `${days.toString().padStart(2, "0")}:${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}
